import { useSnackbar } from 'notistack';
import { useApi } from '../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroPrimaryButton,
} from '../../shared/components';
import { Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
	LenderMatchDrillIn,
	Tier1BankTypeMap,
	Tier1BankTypes,
	Tier3ContactTypeMap,
	Tier3ContactTypes,
} from '../../shared/models';
import { Formik } from 'formik';
import { FormikUtils } from '../../shared/utils';
import { getApiResponseData } from '../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { GetTier1BankTypeValue, GetTier3ContactTypeValue } from '../../shared/utils/enum';
import { QueryKeys } from '../../shared/hooks';
import { LenderMatchRowClickResponse, LenderMatchTableRow } from '../Matching';

interface SearchForLenderInviteDrawerProps {
	dealGuid: string;
	open: boolean;
	onClose: () => void;
}

export const SearchForLenderInviteDrawer: React.FC<SearchForLenderInviteDrawerProps> = ({
	dealGuid,
	open,
	onClose,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();
	const { guid } = useParams<'guid'>();

	// search fields
	const [keywords, setKeywords] = useState<string>('');
	const [searchTier1, setSearchTier1] = useState<string>('Any Tier 1 Type');
	const [searchTier3, setSearchTier3] = useState<string>('Any Tier 3 Type');
	const [searchMode, setSearchMode] = useState<number>(1);
	const [geographyMode, setGeographyMode] = useState<number>(1);

	// display fields
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [results, setResults] = useState<LenderMatchDrillIn[]>([] as LenderMatchDrillIn[]);

	const resetSearchTab = () => {
		setKeywords('');
		setSearchMode(1);
		setSearchTier1('Any Tier 1 Type');
		setSearchTier3('Any Tier 3 Type');
		setResults([] as LenderMatchDrillIn[]);
		setIsSearching(false);
		setGeographyMode(2);
	};

	const searchLenders = () => {
		setIsSearching(true);
		setResults([] as LenderMatchDrillIn[]);
		searchMutation.mutate({
			values: {
				search: encodeURIComponent(keywords),
				tier1: searchTier1,
				tier3: searchTier3,
				mode: searchMode,
				geographyMode: geographyMode,
			},
		} as LendersSearchMutationVariables);
	};

	const searchMutation = useMutation(
		async ({ values }: LendersSearchMutationVariables) => {
			const { data, error } = await getApiResponseData<LenderMatchDrillIn[]>(
				api.get(
					CerebroApiEndpoints.Matching.Search(
						dealGuid,
						values.search,
						GetTier1BankTypeValue(values.tier1 as Tier1BankTypes),
						GetTier3ContactTypeValue(values.tier3 as Tier3ContactTypes),
						values.mode,
						values.geographyMode
					)
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (data: LenderMatchDrillIn[] | null) => {
				setIsSearching(false);
				setResults(data ?? []);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				setIsSearching(false);
			},
		}
	);

	const lendersMutation = useMutation(
		async ({ values }: LendersSearchValuesMutationVariables) => {
			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.LenderInvites.Root(guid), values.bankDealProfileGuid)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (_data, { helpers }) => {
				queryClient.invalidateQueries(QueryKeys.LenderInvites.Root(guid));
				const successMessage = 'Successfully sent selected lenders invites';
				snackbar.enqueueSnackbar(successMessage, {
					variant: 'success',
				});
				helpers.resetForm({
					values: {
						bankDealProfileGuid: [] as string[],
					},
				});
				resetSearchTab();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={onClose} PaperProps={{ sx: { width: '900px' } }}>
			<CerebroHeading size="h1" component="h1">
				Search for Lender
			</CerebroHeading>

			<Grid container spacing={2} alignItems="stretch">
				<Grid item xs={12} lg={2}>
					<CerebroInput
						value={keywords}
						disabled={isSearching}
						onChange={data => setKeywords(data.target.value)}
					/>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={searchTier1}
						disabled={isSearching}
						onChange={data => setSearchTier1(data.target.value as string)}
					>
						<CerebroDropdownMenuItem value="Any Tier 1 Type">Any Tier 1 Type</CerebroDropdownMenuItem>
						{Object.entries(Tier1BankTypeMap).map(([value, description]) => (
							<CerebroDropdownMenuItem key={value} value={value}>
								{description}
							</CerebroDropdownMenuItem>
						))}
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={searchTier3}
						disabled={isSearching}
						onChange={data => setSearchTier3(data.target.value as string)}
					>
						<CerebroDropdownMenuItem value="Any Tier 3 Type">Any Tier 3 Type</CerebroDropdownMenuItem>
						{Object.entries(Tier3ContactTypeMap).map(([value, description]) => (
							<CerebroDropdownMenuItem key={value} value={value}>
								{description}
							</CerebroDropdownMenuItem>
						))}
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={geographyMode}
						onChange={data => setGeographyMode(data.target.value as number)}
					>
						<CerebroDropdownMenuItem value="1">Match Geography</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem value="2">Ignore Geography</CerebroDropdownMenuItem>
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroPrimaryButton
						sx={{ marginLeft: 1, marginTop: 1.5 }}
						disabled={
							isSearching ||
							(!keywords &&
								searchTier1 == 'Any Tier 1 Type' &&
								searchTier3 == 'Any Tier 3 Type' &&
								geographyMode == 2)
						}
						onClick={() => searchLenders()}
					>
						Search
					</CerebroPrimaryButton>
				</Grid>
			</Grid>

			<Formik
				onSubmit={FormikUtils.MutateOnSubmit(lendersMutation.mutate)}
				initialValues={{ bankDealProfileGuid: [] as string[] } as LendersSearchValues}
			>
				{({ isSubmitting, handleSubmit, values, setFieldValue }) => (
					<CerebroForm flatIndicator onSubmit={handleSubmit}>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Select</TableCell>
										<TableCell>Lender Name</TableCell>
										<TableCell>Bank Name</TableCell>
										<TableCell>Tier 3</TableCell>
										<TableCell>Criteria</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{isSearching && (
										<>
											<TableRow>
												<TableCell colSpan={5}>
													<Skeleton />
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell colSpan={5}>
													<Skeleton />
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell colSpan={5}>
													<Skeleton />
												</TableCell>
											</TableRow>
										</>
									)}

									{!isSearching &&
										results.length > 0 &&
										results.map(lender => {
											return (
												<LenderMatchTableRow
													key={lender.bankDealProfileGuid}
													lender={lender}
													isSubmitting={isSearching}
													showCapacityColumn={false}
													allowSelection={true}
													isChecked={
														values.bankDealProfileGuid.find(
															w => w == lender.bankDealProfileGuid
														) != null
													}
													onChange={(item: LenderMatchRowClickResponse) => {
														if (item.isChecked) {
															values.bankDealProfileGuid.push(item.bankDealProfileGuid);
															setFieldValue(
																'bankDealProfileGuid',
																values.bankDealProfileGuid
															);
														} else {
															const id = values.bankDealProfileGuid.indexOf(
																item.bankDealProfileGuid
															);
															if (id > -1) {
																values.bankDealProfileGuid.splice(id, 1);
																setFieldValue(
																	'bankDealProfileGuid',
																	values.bankDealProfileGuid
																);
															}
														}
													}}
												/>
											);
										})}
									{results.length == 0 && (
										<TableRow>
											<TableCell colSpan={5} sx={{ textAlign: 'center' }}>
												No results, please select search criteria.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>

						<CerebroPrimaryButton
							type="submit"
							loading={isSubmitting}
							disabled={!values.bankDealProfileGuid || values.bankDealProfileGuid.length == 0}
							sx={{ marginTop: 6, marginRight: 1 }}
						>
							Send Invite(s)
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type LendersSearchMutationVariables = FormikUtils.MutateOnSubmitVariables<LendersSearch>;
interface LendersSearch {
	search: string;
	tier1: string;
	tier3: string;
	mode: number;
	geographyMode: number;
}

type LendersSearchValuesMutationVariables = FormikUtils.MutateOnSubmitVariables<LendersSearchValues>;
interface LendersSearchValues {
	bankDealProfileGuid: string[];
}
